import { FormInstance, Input, Select } from "antd";
import { Ref } from "react";
import {
  MdInfoOutline,
  MdLanguage,
  MdLocalMovies,
  MdSubject,
  MdTitle,
  MdTranslate,
} from "react-icons/md";
import { Subject } from "rxjs";
import { useAtomValue } from "jotai";
import { masterAtom } from "../../../../atoms/masters.atom";
import { masterToOptions } from "../../../../utils/functions/misc/masterToOptions";
import { SetState, StatePair } from "../../../../types/SetState";
import { TextAreaResize } from "../../../../components/ui/textarea-resize/textarea-resize.component";
import {
  Field,
  FieldGrid,
  HelperRow,
  Section,
} from "../../../films/components/section-card.component";
import { CustomUrlField } from "../../../films/components/custom-url.component";

const SYNOPSIS_LIMIT = 1500;

export const StepOne = ({
  show = true,
  fillHeight,
  synopsis,
  setSynopsis,
  title,
  setTitle,
  form,
  customUrlSubject$,
  customUrlInputRef,
  customUrl,
  urlVerified,
  showVerification,
  onSeriesTitleChange,
  titleFieldsAutoFocus,
}: {
  show?: boolean;
  fillHeight?: boolean;
  form: FormInstance<any>;
  customUrlSubject$: Subject<string>;
  customUrlInputRef: Ref<HTMLInputElement>;
  customUrl: string;
  urlVerified: boolean | null;
  showVerification: boolean;
  onSeriesTitleChange?: (title: string) => void;
  titleFieldsAutoFocus?: boolean;
  setCustomUrl?: SetState<string>;
} & StatePair<"synopsis", "setSynopsis", string> &
  StatePair<"title", "setTitle", string>) => {
  const master = useAtomValue(masterAtom);
  const synopsisCount = synopsis?.length ?? 0;

  return (
    <div
      style={{
        display: show ? "flex" : "none",
        width: "100%",
        height: "100%",
        minHeight: 0,
        flexDirection: "column",
      }}
    >
      <Section
        icon={<MdInfoOutline />}
        title="Series Details"
        subtitle="Tell us about your series — its title, language, genre and a short synopsis."
        fillHeight={fillHeight}
      >
        <FieldGrid>
          <CustomUrlField
            inputRef={customUrlInputRef}
            subject$={customUrlSubject$}
            form={form}
            customUrl={customUrl}
            urlVerified={urlVerified}
            showVerification={showVerification}
            placeholder="your-series-name"
          />
          <Field
            name="title"
            label="Original Title"
            icon={<MdTitle />}
            required
            rules={[
              { required: true, message: "Please enter original title!" },
            ]}
            helper="The title as it appears on the series."
          >
            <Input
              size="large"
              placeholder="Enter original title"
              autoFocus={titleFieldsAutoFocus}
              onChange={(e) => {
                const newTitle = e.target.value;
                setTitle(newTitle);
                onSeriesTitleChange?.(newTitle);
              }}
            />
          </Field>
          <Field
            name="english_title"
            label="English Title"
            icon={<MdTranslate />}
            required
            rules={[
              { required: true, message: "Please enter English title!" },
            ]}
            helper="Use the same as the original title if the series is in English."
          >
            <Input size="large" placeholder="Enter English title" />
          </Field>
          <Field
            name="language"
            label="Original Language"
            icon={<MdLanguage />}
            required
            rules={[
              { required: true, message: "Please select original language!" },
            ]}
            helper="The language the series was originally produced in."
          >
            <Select
              size="large"
              options={masterToOptions("languages", master, [], true)}
              placeholder="Select original language"
              showSearch
              allowClear
            />
          </Field>
          <Field
            name="genre"
            label="Genre"
            icon={<MdLocalMovies />}
            required
            rules={[{ required: true, message: "Please select a genre!" }]}
            helper="Choose all genres that apply."
          >
            <Select
              size="large"
              mode="multiple"
              allowClear
              options={masterToOptions("genre", master)}
              placeholder="Select genres"
              showSearch
            />
          </Field>
          <Field
            name="synopsis"
            label="Synopsis"
            icon={<MdSubject />}
            required
            rules={[{ required: true, message: "Please enter a synopsis" }]}
            helper={
              <HelperRow>
                <span>
                  Audiences see this on the listing page. Hook them in a few
                  sentences.
                </span>
                <span>
                  {synopsisCount} / {SYNOPSIS_LIMIT}
                </span>
              </HelperRow>
            }
            fullWidth
          >
            <TextAreaResize
              value={synopsis}
              onChange={setSynopsis}
              placeholder="What is your series about?"
              rows={4}
              maxLength={SYNOPSIS_LIMIT}
            />
          </Field>
        </FieldGrid>
      </Section>
    </div>
  );
};
